import { ManualProductLayoutSelector, ProductSelectorDisabledOverlay } from '@app/components/cms/ManualLayoutEditor/ManualLayoutSelector';
import { CmsProduct } from '@app/components/products/CmsProduct/CmsProduct';
import { getCmsSectionById, getManualLayoutEditorBlockTypeToSelect, getSectionIdByGroupId } from '@app/ducks';
import { getCmsProducts } from '@app/ducks/cms/products';
import { getCurrentPricelist } from '@app/ducks/pricelists';
import { getProductBlockHeight, getScreenRelativeStyles } from '@app/logic/pages';
import { convertCmsProductToStandard, getUrlForProductAndVariant } from '@app/logic/products';
import { useSpecifiedDeviceWidth } from '@app/utils/hooks';
import { isDefined } from '@app/utils/is';
import { isEveryElementInArray } from '@app/utils/isEveryElementInArray';
import { Cms, CmsBlocks } from '@typings';
import React from 'react';
import { useSelector } from 'react-redux';

import { BlockWarnings } from './BlockWarnings';
import { ProductBlockTools } from './ProductBlockTools';

export const EditorProductBlock = (props: CmsBlocks.EditorBlockProps<Cms.ProductBlock>) => {
  const { parameters, rowHeight, isManualLayoutEditing } = props;
  const { position, groupId, settings, id } = parameters;
  const { products: productsList } = settings;

  const screenWidth = useSpecifiedDeviceWidth();
  const cmsProducts = useSelector(getCmsProducts);
  const currentPricelist = useSelector(getCurrentPricelist);
  const sectionId = useSelector(getSectionIdByGroupId(groupId));
  const section = useSelector(getCmsSectionById(sectionId ?? null));
  const blockTypeToSelect = useSelector(getManualLayoutEditorBlockTypeToSelect);
  const screenRelativePosition = getScreenRelativeStyles(screenWidth, position);

  const calculatedHeight = getProductBlockHeight(rowHeight, screenRelativePosition);
  const blockHeight = typeof calculatedHeight === 'number' ? Math.round(calculatedHeight) : calculatedHeight;

  const products = React.useMemo(() => convertCmsProductToStandard(cmsProducts, currentPricelist), [cmsProducts, currentPricelist]);

  const shouldSelectProducts = blockTypeToSelect === 'product';

  return (
    <>
      <div
        className="grid content-start gap-8"
        style={{
          gridTemplateColumns: `repeat(${screenRelativePosition.spanX}, minmax(0, 1fr))`,
          height: blockHeight,
        }}
      >
        {isManualLayoutEditing && !shouldSelectProducts && <ProductSelectorDisabledOverlay />}
        {productsList.map(product => {
          const isInCorrectMarket =
            isDefined(section) &&
            isEveryElementInArray(
              section.visibleFor.marketIds,
              cmsProducts[product]?.markets.map(market => market.toString()),
            );

          const productData = products[product];

          if (!isDefined(productData)) {
            return null;
          }

          const productStyles = { height: rowHeight };

          const productElement = (
            <CmsProduct
              key={product}
              isInEditor
              isInvalid={!isInCorrectMarket}
              style={productStyles}
              productInfo={productData}
              getProductUrl={getUrlForProductAndVariant}
            />
          );

          if (isManualLayoutEditing) {
            return (
              <div key={product} style={productStyles} className="relative mx-4 mb-8">
                {shouldSelectProducts && <ManualProductLayoutSelector blockId={id} productId={product} />}
                {productElement}
              </div>
            );
          }

          return productElement;
        })}
      </div>
      <ProductBlockTools {...props} />
      <BlockWarnings {...props} />
    </>
  );
};
